import React, { useMemo, useRef, useState } from "react";
import { Grid, LinearProgress, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  MdCheckCircle,
  MdOutlineFileDownload,
  MdOutlineFileUpload,
} from "react-icons/md";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import {
  downloadProcessFileApi,
  uploadRequestFileApi,
} from "../../../services/RequestAccess/RequestAccess";
import { triggerToast } from "../../../utils/toast";
import * as styles from "./HandleFileSelect.module.scss";
import { TableHeader } from "cprime-datatable-components";
import GridTableCommon from "../../../Components/GridTableCommon/GridTableCommon";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import Typography from "../../../Components/Common/CprimeText";

const ExcelUploader = ({
  setIsSubmitEnabled,
  step,
  setStep,
  isMock = false,
  setSelectedRows,
  selectedRows,
}: {
  setIsSubmitEnabled: () => void;
  step: string;
  setStep: any;
  isMock?: boolean;
  setSelectedRows?: any;
  selectedRows?: any;
}) => {
  const scssClass = styles as any;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [apiCallStatus, setApiCallStatus] = useState<string>("");
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [recordCounts, setRecordCounts] = useState<any>({});
  const [fileKey, setFileKey] = useState<any>({});
  const [gridRows, setGridRows] = useState<any>([]);
  const [fileName, setFileName] = useState<string>("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 10 });
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [isError, setError] = useState<any>("");
  const [hasError, setHasError] = useState<any>("");
  const [selectedData, setSelectedData] = useState<any[]>([]);

  const gridData: GridTableDataType = useMemo(
    () => ({
      columns: [
        { id: "uploadStatus", name: "Upload Status", type: "ICON_LABEL" },
        { id: "workday_id", name: "Workday ID", type: "DEFAULT" },
        { id: "user_email_id", name: "Email", type: "DEFAULT" },
        { id: "feature", name: "Feature", type: "DEFAULT" },
        { id: "permissions", name: "Permissions", type: "DEFAULT" },
        { id: "justification", name: "Justfication", type: "DEFAULT" },
      ],

      rows: gridRows.slice(
        pagination.page * pagination.rowsPerPage,
        pagination.page * pagination.rowsPerPage + pagination.rowsPerPage,
      ),
      totalCount: gridRows?.length,
    }),
    [pagination, gridRows],
  );

  const downloadFileUpload = async () => {
    try {
      if (fileKey) {
        const response = (await downloadProcessFileApi({
          fileKey,
        })) as any;
        if (response.status !== 201) {
          triggerToast(
            "Failed to download file. Check token or permissions.",
            "error",
          );
          return;
        }

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const fileUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = "request access.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        triggerToast("File downloaded successfully", "success");
      }
    } catch (err) {}
  };

  const resetUpload = () => {
    setUploadPercentage(0);
    setGridRows([]);
    setFileName("");
    setStep("upload");
  };

  const handleFile = async (file: File) => {
    if (!file || !file.name?.endsWith(".xlsx")) {
      triggerToast("Please upload a valid Excel file (.xlsx)", "error");
      return;
    }

    const fileSize = file?.size ?? 0;
    if (fileSize > 800 * 1024) {
      const fileSizeMB = (fileSize / 1024 / 1024).toFixed(2);
      triggerToast(
        "File size exceeds 800KB. Please upload a smaller file.",
        "error",
      );
      setHasError(
        `File exceeds ${fileSizeMB} MB. Please upload a smaller file.`,
      );
      if (fileInputRef?.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    try {
      setStep("processing");
      setUploadPercentage(0);
      setFileName(file?.name);
      setError("");

      // Upload the file
      const formData = new FormData();
      formData?.append("file", file);
      setApiCallStatus("uploading");
      const res = await uploadRequestFileApi(formData, (progressEvent: any) => {
        const loaded = progressEvent?.loaded ?? 0;
        const total = progressEvent?.total ?? 1;
        const percent = Math.round((loaded * 100) / total);
        setUploadPercentage(percent);
      });

      // setUploadPercentage(100);
      // const res = fileUploadResponse;
      if (res?.error) {
        setApiCallStatus("Failed");
        triggerToast(res?.message || "File uploaded failed", "error");
        setError(res?.error);
      } else {
        setIsSubmitEnabled();
        setApiCallStatus("Success");
        setStep("done");
        setRecordCounts(res?.data?.recordCounts);
        setFileKey(res?.data?.processedFile?.fileKey);
        const rows = res?.data?.validation?.rowResults?.map((row: any) => ({
          uploadStatus: row?.errors?.[0] || "Valid",
          workday_id: row?.data?.workday_id,
          user_email_id: row?.data?.user_email_id,
          feature: row?.data?.feature,
          permissions: row?.data?.permissions,
          justification: row?.data?.justification,
          isSelectable: row?.isValid ? null : false,
          featureId: row?.data?.featureId,
          permissionIds: row?.data?.permissionIds,
          displayName: row?.data?.displayName,
          isValid: row?.isValid,
          recordId: `${row?.data?.rowNum}_${row?.data?.user_email_id}_${row?.data?.workday_id}`,
        }));
        setGridRows(rows);
        triggerToast(res?.message || "File uploaded successfully", "success");
      }
    } catch (error) {
      setApiCallStatus("Failed");
      triggerToast("Upload error", "error");
      return;
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e?.preventDefault();
    const file = e?.dataTransfer?.files[0];
    handleFile(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0] as File;
    handleFile(file);
  };

  return (
    <div>
      {step === "upload" ? (
        <>
          <div className={scssClass?.font14}>
            Upload Excel file of users to request access for, this sheet must
            have for each user:
          </div>
          <ul className={scssClass?.font14}>
            <li>Workday ID</li>
            <li>Email</li>
            <li>Features and Permissions being requested</li>
          </ul>
        </>
      ) : step === "processing" ? (
        <div className={scssClass?.font14}>
          Please do not leave the page. Large files may take longer to process.
        </div>
      ) : (
        <></>
      )}
      {/* STEP 1: File Upload */}
      {step === "upload" && (
        <>
          <div
            style={{
              fontSize: "14px",
              lineHeight: "20px",
              marginBottom: "8px",
            }}
          >
            Limit one file. Max file size is 800kb.
          </div>
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            data-testid="inputFileContainer"
            onClick={() => fileInputRef?.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e?.preventDefault()}
            className={`${scssClass?.fileDragDropContainer} ${hasError ? scssClass?.fileError : ""}`}
          >
            <input
              type="file"
              data-testid="inputFileId"
              accept=".xlsx"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <div
              style={{
                display: "grid",
                textAlign: "center",
                lineHeight: "20px",
              }}
            >
              <span>
                <MdOutlineFileUpload
                  style={{ fontSize: "20px", color: "#2469FF" }}
                />
              </span>
              <span style={{ color: "#2469FF" }}>Choose a file</span>
              <span>or drag and drop files here.</span>
            </div>
          </Grid>
          {hasError ? (
            <div className={scssClass?.hasError}>{hasError}</div>
          ) : null}
        </>
      )}

      {/* STEP 2: Progress + Table */}
      {(step === "processing" || step === "done") && (
        <>
          <div
            style={{
              marginTop: "10px",
              position: "relative",
              background: "#F1F2F4",
              padding: "1rem",
              borderRadius: "3px",
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="body2">{fileName}</Typography>
              </Grid>
              <Grid item display="flex" gap="4px">
                {step === "done" ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <Typography variant="body2">100%</Typography>
                    <MdCheckCircle
                      style={{ color: "green", fontSize: "16px" }}
                    />
                  </div>
                ) : (
                  <Typography variant="body2" style={{ color: "#555" }}>
                    {uploadPercentage > 0 && uploadPercentage < 100
                      ? `${uploadPercentage}%`
                      : apiCallStatus === "Failed"
                        ? apiCallStatus
                        : uploadPercentage === 100 &&
                            apiCallStatus === "uploading"
                          ? "Finishing..."
                          : ""}
                  </Typography>
                )}
                <CloseIcon
                  data-testid="closebutton"
                  fontSize="small"
                  onClick={resetUpload}
                  style={{ cursor: "pointer" }}
                />
              </Grid>
            </Grid>

            <div style={{ position: "relative", marginTop: "4px" }}>
              <LinearProgress
                variant="determinate"
                value={uploadPercentage}
                sx={{
                  height: "4px",
                  backgroundColor: "#fff",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: step === "done" ? "#4caf50" : "#000",
                  },
                }}
              />
            </div>
          </div>
          {isError && (
            <Typography sx={{ color: "red", marginTop: "8px" }}>
              {isError}
            </Typography>
          )}
        </>
      )}
      {isMock ? (
        <div
          data-testid="set-preview"
          onClick={() => setStep("preview")}
          role="button"
          tabIndex={0}
        />
      ) : null}

      {/* STEP 2: Data Table */}
      {step === "preview" && (
        <Paper style={{ marginTop: "0px", overflowX: "auto" }}>
          <TableHeader
            title="All Requests"
            details={[
              `${recordCounts?.total || 0} Items`,
              `${recordCounts?.valid || 0} Valid`,
              `${recordCounts?.invalid || 0} Invalid`,
              `${recordCounts?.missing || 0} Missing`,
            ]}
            rightSlot={
              <MdOutlineFileDownload
                style={{ fontSize: "20px", cursor: "pointer" }}
                data-testid="download-file-button"
                onClick={downloadFileUpload}
              />
            }
          />
          <GridTableCommon
            gridData={gridData ?? {}}
            selectedRows={selectedData}
            onSelectionChange={setSelectedData}
            setSelectedData={setSelectedRows}
            selectedData={selectedRows}
            isRowValid={(row) => row?.isValid}
            tableHeader={false}
            keyId="recordId"
            setIsSubmitEnabled={setIsSubmitEnabled}
            filters={filters}
            sorting={sorting}
            setSorting={setSorting}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Paper>
      )}
    </div>
  );
};

export default ExcelUploader;
